/* ---
   EcoDriveSmart Motion Controller
   assets/eds-motion.js
   Loaded with defer in layout/theme.liquid.
   Mounts on DOMContentLoaded and shopify:section:load.
   Tears down on shopify:section:unload.
--- */

(function (window, document) {
  'use strict';

  if (window.EDSMotion && window.EDSMotion._loaded) { return; }

  /* === Registries === */
  var initRegistry = new WeakMap();
  var cleanupRegistry = new WeakMap();

  function isInitialized(el, key) {
    var bag = initRegistry.get(el);
    return !!(bag && bag[key]);
  }
  function markInitialized(el, key) {
    var bag = initRegistry.get(el);
    if (!bag) { bag = {}; initRegistry.set(el, bag); }
    bag[key] = true;
  }
  function registerCleanup(el, fn) {
    var arr = cleanupRegistry.get(el);
    if (!arr) { arr = []; cleanupRegistry.set(el, arr); }
    arr.push(fn);
  }
  function runCleanup(el) {
    var arr = cleanupRegistry.get(el);
    if (arr) {
      for (var i = 0; i < arr.length; i++) {
        try { arr[i](); } catch (e) {}
      }
      cleanupRegistry['delete'](el);
    }
    initRegistry['delete'](el);
  }

  /* === Utilities === */
  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }
  function hasFinePointer() {
    return !!(window.matchMedia && window.matchMedia('(pointer: fine)').matches);
  }
  function clamp(v, min, max) {
    return Math.max(min, Math.min(max, v));
  }
  function easeOutCubic(t) {
    var p = 1 - t;
    return 1 - p * p * p;
  }
  function seededRandom(seed) {
    var s = (Math.abs(seed) % 2147483647) || 1;
    return function () {
      s = (s * 16807) % 2147483647;
      return (s - 1) / 2147483646;
    };
  }
  function rafThrottle(fn) {
    var ticking = false;
    return function () {
      if (ticking) return;
      ticking = true;
      var ctx = this;
      var args = arguments;
      window.requestAnimationFrame(function () {
        fn.apply(ctx, args);
        ticking = false;
      });
    };
  }
  function nextFrame(fn) {
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(fn);
    });
  }

  /* === CAD Field SVG generator (procedural, seeded) === */
  function generateCADSVG(seed, density) {
    var rand = seededRandom(seed);
    var w = 1200;
    var h = 800;
    var parts = [];
    parts.push('<svg viewBox="0 0 ' + w + ' ' + h + '" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">');

    /* Long orthogonal traces */
    var traceCount = density * 4;
    for (var i = 0; i < traceCount; i++) {
      var horizontal = rand() > 0.5;
      var x1, y1, x2, y2;
      if (horizontal) {
        y1 = Math.floor(rand() * h);
        y2 = y1;
        x1 = Math.floor(rand() * w * 0.6);
        x2 = x1 + 120 + Math.floor(rand() * w * 0.35);
      } else {
        x1 = Math.floor(rand() * w);
        x2 = x1;
        y1 = Math.floor(rand() * h * 0.6);
        y2 = y1 + 100 + Math.floor(rand() * h * 0.35);
      }
      var accent = (i % 9 === 0) ? ' class="eds-cad-accent"' : '';
      parts.push('<path d="M ' + x1 + ' ' + y1 + ' L ' + x2 + ' ' + y2 + '"' + accent + '/>');
    }

    /* L-shaped trace bends */
    for (var j = 0; j < density * 2; j++) {
      var sx = Math.floor(rand() * w);
      var sy = Math.floor(rand() * h);
      var len1 = Math.floor(rand() * 220) + 60;
      var len2 = Math.floor(rand() * 220) + 60;
      var dirH = rand() > 0.5 ? 1 : -1;
      var dirV = rand() > 0.5 ? 1 : -1;
      parts.push('<path d="M ' + sx + ' ' + sy + ' l ' + (len1 * dirH) + ' 0 l 0 ' + (len2 * dirV) + '"/>');
    }

    /* Component circles */
    var circleCount = Math.floor(density * 1.5);
    for (var k = 0; k < circleCount; k++) {
      var cx = Math.floor(rand() * w);
      var cy = Math.floor(rand() * h);
      var r = 4 + Math.floor(rand() * 9);
      parts.push('<circle cx="' + cx + '" cy="' + cy + '" r="' + r + '"/>');
    }

    /* Registration ticks */
    for (var m = 0; m < density; m++) {
      var tx = Math.floor(rand() * w);
      var ty = Math.floor(rand() * h);
      parts.push('<path d="M ' + tx + ' ' + ty + ' l 14 0 M ' + tx + ' ' + (ty - 7) + ' l 0 14"/>');
    }

    parts.push('</svg>');
    return parts.join('');
  }

  /* === Controllers === */

  function initCADField(el) {
    if (isInitialized(el, 'cad-field')) return;
    markInitialized(el, 'cad-field');

    var density = clamp(parseInt(el.getAttribute('data-eds-cad-density'), 10) || 8, 5, 12);
    var seed = parseInt(el.getAttribute('data-eds-cad-seed'), 10) || 42;
    var stroke = clamp(parseFloat(el.getAttribute('data-eds-cad-stroke')) || 1.25, 1.0, 1.5);
    var opacityAttr = parseFloat(el.getAttribute('data-eds-cad-opacity'));
    if (!isNaN(opacityAttr)) {
      el.style.setProperty('--eds-cad-opacity', clamp(opacityAttr, 0.10, 0.20));
    }
    el.style.setProperty('--eds-cad-stroke', stroke);

    el.innerHTML = generateCADSVG(seed, density);

    var animatables = el.querySelectorAll('path, circle');
    var lengths = [];
    for (var p = 0; p < animatables.length; p++) {
      var len = 200;
      try {
        if (animatables[p].getTotalLength) {
          len = animatables[p].getTotalLength() || 200;
        }
      } catch (e) {}
      lengths.push(len);
      animatables[p].style.strokeDasharray = len;
      animatables[p].style.strokeDashoffset = len;
    }

    if (prefersReducedMotion()) {
      for (var s = 0; s < animatables.length; s++) {
        animatables[s].style.strokeDashoffset = 0;
      }
      return;
    }

    var triggered = false;
    var io = new IntersectionObserver(function (entries) {
      for (var e = 0; e < entries.length; e++) {
        if (entries[e].isIntersecting && !triggered) {
          triggered = true;
          for (var a = 0; a < animatables.length; a++) {
            (function (path, idx, len) {
              var delay = clamp(idx * 6, 0, 1400);
              var duration = clamp(900 + len * 0.9, 900, 2200);
              window.setTimeout(function () {
                path.style.transition = 'stroke-dashoffset ' + duration + 'ms cubic-bezier(0.22, 1, 0.36, 1)';
                path.style.strokeDashoffset = 0;
              }, delay);
            })(animatables[a], a, lengths[a]);
          }
          io.disconnect();
        }
      }
    }, { threshold: 0.15 });
    io.observe(el);
    registerCleanup(el, function () { io.disconnect(); });
  }

  function initCADTrace(el) {
    if (isInitialized(el, 'cad-trace')) return;
    markInitialized(el, 'cad-trace');

    var paths = el.querySelectorAll('path, line, polyline');
    for (var i = 0; i < paths.length; i++) {
      var len = 200;
      try {
        if (paths[i].getTotalLength) {
          len = paths[i].getTotalLength() || 200;
        }
      } catch (e) {}
      paths[i].style.strokeDasharray = len;
      paths[i].style.strokeDashoffset = len;
    }

    if (prefersReducedMotion()) {
      el.classList.add('eds-is-traced');
      for (var r = 0; r < paths.length; r++) { paths[r].style.strokeDashoffset = 0; }
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      for (var e = 0; e < entries.length; e++) {
        if (entries[e].isIntersecting) {
          el.classList.add('eds-is-traced');
          for (var t = 0; t < paths.length; t++) { paths[t].style.strokeDashoffset = 0; }
          io.disconnect();
        }
      }
    }, { threshold: 0.2 });
    io.observe(el);
    registerCleanup(el, function () { io.disconnect(); });
  }

  function initReveal(el) {
    if (isInitialized(el, 'reveal')) return;
    markInitialized(el, 'reveal');

    if (prefersReducedMotion()) {
      el.classList.add('eds-is-revealed');
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      for (var e = 0; e < entries.length; e++) {
        if (entries[e].isIntersecting) {
          el.classList.add('eds-is-revealed');
          io.disconnect();
        }
      }
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    io.observe(el);
    registerCleanup(el, function () { io.disconnect(); });
  }

  function initStagger(el) {
    if (isInitialized(el, 'stagger')) return;
    markInitialized(el, 'stagger');

    var step = parseInt(el.getAttribute('data-eds-stagger-step'), 10) || 80;
    var children = el.children;
    for (var i = 0; i < children.length; i++) {
      var delay = (i * step) + 'ms';
      children[i].style.setProperty('--eds-delay', delay);
      children[i].style.transitionDelay = delay;
      if (!children[i].hasAttribute('data-eds-reveal')) {
        children[i].setAttribute('data-eds-reveal', '');
      }
    }
  }

  function initSpecCard(el) {
    if (isInitialized(el, 'spec-card')) return;
    markInitialized(el, 'spec-card');

    if (!el.querySelector('.eds-scan-line')) {
      var scan = document.createElement('span');
      scan.className = 'eds-scan-line';
      scan.setAttribute('aria-hidden', 'true');
      el.appendChild(scan);
    }
    var positions = ['tl', 'tr', 'bl', 'br'];
    for (var i = 0; i < positions.length; i++) {
      if (!el.querySelector('.eds-bracket-' + positions[i])) {
        var b = document.createElement('span');
        b.className = 'eds-bracket eds-bracket-' + positions[i];
        b.setAttribute('aria-hidden', 'true');
        el.appendChild(b);
      }
    }
  }

  function initDragRail(el) {
    if (isInitialized(el, 'drag-rail')) return;
    markInitialized(el, 'drag-rail');

    var isDown = false;
    var startX = 0;
    var startScroll = 0;
    var moved = false;
    var pointerId = null;

    function onDown(ev) {
      if (ev.button !== undefined && ev.button !== 0) return;
      isDown = true;
      moved = false;
      startX = ev.pageX;
      startScroll = el.scrollLeft;
      pointerId = ev.pointerId;
      /* Do NOT capture the pointer here. Capturing on pointerdown retargets the
         following pointerup to the rail, and the browser then fires the click on
         the rail instead of the card control under the pointer, so Add to cart
         buttons and product links never receive their click. Capture is deferred
         to onMove, and only once an actual drag is underway. */
    }
    function onMove(ev) {
      if (!isDown) return;
      var dx = ev.pageX - startX;
      if (!moved && Math.abs(dx) > 6) {
        moved = true;
        el.classList.add('eds-is-dragging');
        /* A real drag has started. Capture now so the gesture keeps tracking
           even if the pointer leaves the rail. A plain click never reaches this
           branch, so its click stays on the control and fires normally. */
        if (el.setPointerCapture && pointerId != null) {
          try { el.setPointerCapture(pointerId); } catch (e) {}
        }
      }
      if (moved) {
        if (ev.cancelable) { ev.preventDefault(); }
        el.scrollLeft = startScroll - dx;
      }
    }
    function onUp() {
      if (!isDown) return;
      isDown = false;
      var wasMoved = moved;
      moved = false;
      if (el.releasePointerCapture && pointerId != null) {
        try { el.releasePointerCapture(pointerId); } catch (e) {}
      }
      pointerId = null;
      el.classList.remove('eds-is-dragging');
      if (wasMoved) {
        var suppress = function (clickEv) {
          clickEv.preventDefault();
          clickEv.stopPropagation();
          el.removeEventListener('click', suppress, true);
        };
        el.addEventListener('click', suppress, true);
        window.setTimeout(function () {
          el.removeEventListener('click', suppress, true);
        }, 60);
      }
    }

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove, { passive: false });
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    el.addEventListener('pointerleave', onUp);
    registerCleanup(el, function () {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      el.removeEventListener('pointerleave', onUp);
    });
  }

  function initMagnetic(el) {
    if (isInitialized(el, 'magnetic')) return;
    markInitialized(el, 'magnetic');
    if (prefersReducedMotion() || !hasFinePointer()) return;

    var strength = clamp(parseFloat(el.getAttribute('data-eds-magnetic-strength')) || 0.25, 0, 0.6);

    function onMove(ev) {
      var rect = el.getBoundingClientRect();
      var cx = rect.left + rect.width / 2;
      var cy = rect.top + rect.height / 2;
      var dx = (ev.clientX - cx) * strength;
      var dy = (ev.clientY - cy) * strength;
      el.style.transform = 'translate3d(' + dx.toFixed(2) + 'px, ' + dy.toFixed(2) + 'px, 0)';
    }
    function onLeave() {
      el.style.transform = 'translate3d(0, 0, 0)';
    }

    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    registerCleanup(el, function () {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
      el.style.transform = '';
    });
  }

  function initSmartHeader(el) {
    if (isInitialized(el, 'smart-header')) return;
    markInitialized(el, 'smart-header');

    var threshold = parseInt(el.getAttribute('data-eds-hide-after'), 10) || 260;
    var lastY = window.pageYOffset || 0;

    function update() {
      var y = window.pageYOffset || 0;
      if (y > 24) {
        el.classList.add('eds-is-scrolled');
      } else {
        el.classList.remove('eds-is-scrolled');
      }
      if (y > threshold && y > lastY + 4) {
        el.classList.add('eds-is-hidden');
      } else if (y < lastY - 4 || y < threshold) {
        el.classList.remove('eds-is-hidden');
      }
      lastY = y;
    }

    var throttled = rafThrottle(update);
    window.addEventListener('scroll', throttled, { passive: true });
    update();
    registerCleanup(el, function () {
      window.removeEventListener('scroll', throttled);
    });
  }

  function initProductImage(el) {
    if (isInitialized(el, 'product-image')) return;
    markInitialized(el, 'product-image');

    if (prefersReducedMotion()) {
      el.classList.add('eds-is-pi-revealed');
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      for (var e = 0; e < entries.length; e++) {
        if (entries[e].isIntersecting) {
          el.classList.add('eds-is-pi-revealed');
          io.disconnect();
        }
      }
    }, { threshold: 0.15 });
    io.observe(el);
    registerCleanup(el, function () { io.disconnect(); });
  }

  function initCounter(el) {
    if (isInitialized(el, 'counter')) return;
    markInitialized(el, 'counter');

    var rawTarget = el.getAttribute('data-eds-counter-to');
    var target;
    if (rawTarget !== null && rawTarget !== '') {
      target = parseFloat(rawTarget);
    } else {
      target = parseFloat((el.textContent || '').replace(/[^0-9.\-]/g, ''));
    }
    if (isNaN(target)) return;

    var prefix = el.getAttribute('data-eds-counter-prefix') || '';
    var suffix = el.getAttribute('data-eds-counter-suffix') || '';
    var decimals = parseInt(el.getAttribute('data-eds-counter-decimals'), 10);
    if (isNaN(decimals)) {
      var fStr = String(target);
      var dot = fStr.indexOf('.');
      decimals = dot === -1 ? 0 : (fStr.length - dot - 1);
    }
    var duration = 1400;

    function render(v) {
      el.textContent = prefix + v.toFixed(decimals) + suffix;
    }

    if (prefersReducedMotion()) {
      render(target);
      return;
    }
    render(0);

    var started = false;
    var io = new IntersectionObserver(function (entries) {
      for (var e = 0; e < entries.length; e++) {
        if (entries[e].isIntersecting && !started) {
          started = true;
          var t0 = null;
          var step = function (ts) {
            if (t0 === null) { t0 = ts; }
            var t = (ts - t0) / duration;
            if (t >= 1) { render(target); return; }
            render(target * easeOutCubic(t));
            window.requestAnimationFrame(step);
          };
          window.requestAnimationFrame(step);
          io.disconnect();
        }
      }
    }, { threshold: 0.4 });
    io.observe(el);
    registerCleanup(el, function () { io.disconnect(); });
  }

  function initLineTrace(el) {
    if (isInitialized(el, 'line-trace')) return;
    markInitialized(el, 'line-trace');

    if (prefersReducedMotion()) {
      el.classList.add('eds-is-line-active');
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      for (var e = 0; e < entries.length; e++) {
        if (entries[e].isIntersecting) {
          el.classList.add('eds-is-line-active');
          io.disconnect();
        }
      }
    }, { threshold: 0.3 });
    io.observe(el);
    registerCleanup(el, function () { io.disconnect(); });
  }

  /* === Global UI patterns === */

  function ensureBackdrop() {
    var bd = document.querySelector('.eds-backdrop');
    if (bd) return bd;
    bd = document.createElement('div');
    bd.className = 'eds-backdrop';
    bd.setAttribute('aria-hidden', 'true');
    bd.addEventListener('click', function () { closeAllPanels(); });
    document.body.appendChild(bd);
    return bd;
  }

  function ensureToastHost() {
    var host = document.querySelector('.eds-toast-host');
    if (host) return host;
    host = document.createElement('div');
    host.className = 'eds-toast-host';
    host.setAttribute('aria-live', 'polite');
    host.setAttribute('aria-atomic', 'true');
    document.body.appendChild(host);
    return host;
  }

  function applyPanelStagger(panel) {
    var cartItems = panel.querySelectorAll('.eds-cart-item');
    for (var i = 0; i < cartItems.length; i++) {
      cartItems[i].style.transitionDelay = (i * 80) + 'ms';
    }
    var navItems = panel.querySelectorAll('.eds-mobile-nav__item');
    for (var n = 0; n < navItems.length; n++) {
      navItems[n].style.transitionDelay = (n * 60) + 'ms';
    }
    var searchResults = panel.querySelectorAll('.eds-search-overlay__result');
    var maxResults = Math.min(searchResults.length, 5);
    for (var r = 0; r < maxResults; r++) {
      searchResults[r].style.transitionDelay = (r * 70) + 'ms';
    }
  }

  function openOverlay(el) {
    if (!el) return;
    ensureBackdrop().classList.add('eds-is-open');
    el.classList.add('eds-is-open');
    document.documentElement.classList.add('eds-scroll-lock');
    applyPanelStagger(el);
    bindEscOnce();
    var focusable = el.querySelector('input, button, [href], [tabindex]:not([tabindex="-1"])');
    if (focusable) {
      window.setTimeout(function () { try { focusable.focus(); } catch (e) {} }, 320);
    }
  }

  function closeOverlay(el) {
    if (!el) return;
    el.classList.remove('eds-is-open');
    if (!anyOverlayOpen()) {
      var bd = document.querySelector('.eds-backdrop');
      if (bd) { bd.classList.remove('eds-is-open'); }
      document.documentElement.classList.remove('eds-scroll-lock');
    }
  }

  function anyOverlayOpen() {
    return !!(
      document.querySelector('[data-eds-cart-drawer].eds-is-open') ||
      document.querySelector('[data-eds-mobile-nav].eds-is-open') ||
      document.querySelector('.eds-search-overlay.eds-is-open')
    );
  }

  function closeAllPanels() {
    var cart = document.querySelector('[data-eds-cart-drawer]');
    var nav = document.querySelector('[data-eds-mobile-nav]');
    var search = document.querySelector('.eds-search-overlay');
    if (cart) { closeOverlay(cart); }
    if (nav) { closeOverlay(nav); }
    if (search) { closeOverlay(search); }
  }

  var escBound = false;
  function bindEscOnce() {
    if (escBound) return;
    escBound = true;
    document.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape' || ev.keyCode === 27) { closeAllPanels(); }
    });
  }

  function initAnnouncementBar() {
    var bar = document.querySelector('.eds-announce-bar');
    if (!bar) return;
    if (isInitialized(bar, 'announce-bar')) return;
    markInitialized(bar, 'announce-bar');

    try {
      if (window.sessionStorage && window.sessionStorage.getItem('eds-announce-dismissed') === '1') {
        bar.style.display = 'none';
        return;
      }
    } catch (e) {}

    var messages = bar.querySelectorAll('.eds-announce-bar__msg');
    if (!messages.length) return;
    messages[0].classList.add('eds-is-current');

    var currentIdx = 0;
    if (messages.length > 1 && !prefersReducedMotion()) {
      var intervalId = window.setInterval(function () {
        var nextIdx = (currentIdx + 1) % messages.length;
        for (var i = 0; i < messages.length; i++) {
          if (i !== currentIdx && i !== nextIdx) {
            messages[i].classList.remove('eds-is-leaving');
            messages[i].classList.remove('eds-is-current');
          }
        }
        messages[currentIdx].classList.remove('eds-is-current');
        messages[currentIdx].classList.add('eds-is-leaving');
        messages[nextIdx].classList.add('eds-is-current');
        currentIdx = nextIdx;
      }, 5800);
      registerCleanup(bar, function () { window.clearInterval(intervalId); });
    }

    var closer = bar.querySelector('.eds-announce-bar__close, [data-eds-announce-close]');
    if (closer) {
      var onClose = function () {
        bar.style.display = 'none';
        try { window.sessionStorage.setItem('eds-announce-dismissed', '1'); } catch (e) {}
      };
      closer.addEventListener('click', onClose);
      registerCleanup(bar, function () { closer.removeEventListener('click', onClose); });
    }
  }

  function showToast(message, type) {
    var host = ensureToastHost();
    var toast = document.createElement('div');
    toast.className = 'eds-toast';
    if (type === 'error') { toast.className += ' eds-toast--error'; }
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');
    toast.textContent = String(message);
    host.appendChild(toast);
    nextFrame(function () { toast.classList.add('eds-is-visible'); });
    window.setTimeout(function () {
      toast.classList.remove('eds-is-visible');
      toast.classList.add('eds-toast--leaving');
      window.setTimeout(function () {
        if (toast.parentNode) { toast.parentNode.removeChild(toast); }
      }, 420);
    }, 3400);
  }

  /* === Cart integration === */
  function addToCart(item) {
    var title = item && item.title ? item.title : 'Item';
    if (item && item.variantId && window.fetch) {
      var body = 'id=' + encodeURIComponent(item.variantId) + '&quantity=' + (parseInt(item.quantity, 10) || 1);
      window.fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
        body: body
      }).then(function (res) {
        if (!res.ok) { throw new Error('add failed'); }
        return res.json();
      }).then(function (data) {
        document.dispatchEvent(new CustomEvent('eds:cart:added', { detail: { item: item, response: data } }));
        showToast('Added to cart: ' + title, 'success');
        EDSPatterns.openCart();
      })['catch'](function () {
        showToast('Could not add to cart. Please try again.', 'error');
      });
    } else {
      document.dispatchEvent(new CustomEvent('eds:cart:added', { detail: { item: item } }));
      showToast('Added to cart: ' + title, 'success');
      EDSPatterns.openCart();
    }
  }

  /* === Public API === */
  var EDSPatterns = {
    openCart: function () { openOverlay(document.querySelector('[data-eds-cart-drawer]')); },
    closeCart: function () { closeOverlay(document.querySelector('[data-eds-cart-drawer]')); },
    addToCart: addToCart,
    openMobileNav: function () { openOverlay(document.querySelector('[data-eds-mobile-nav]')); },
    closeMobileNav: function () { closeOverlay(document.querySelector('[data-eds-mobile-nav]')); },
    openSearch: function () { openOverlay(document.querySelector('.eds-search-overlay')); },
    closeSearch: function () { closeOverlay(document.querySelector('.eds-search-overlay')); },
    toast: showToast,
    closeAll: closeAllPanels
  };

  /* === Action delegation === */
  function onActionClick(ev) {
    var trigger = ev.target && ev.target.closest ? ev.target.closest('[data-eds-action]') : null;
    if (!trigger) return;
    var action = trigger.getAttribute('data-eds-action');
    switch (action) {
      case 'open-search': ev.preventDefault(); EDSPatterns.openSearch(); break;
      case 'close-search': ev.preventDefault(); EDSPatterns.closeSearch(); break;
      case 'open-cart': ev.preventDefault(); EDSPatterns.openCart(); break;
      case 'close-cart': ev.preventDefault(); EDSPatterns.closeCart(); break;
      case 'open-mobile-nav': ev.preventDefault(); EDSPatterns.openMobileNav(); break;
      case 'close-mobile-nav': ev.preventDefault(); EDSPatterns.closeMobileNav(); break;
      case 'add-to-cart':
        ev.preventDefault();
        EDSPatterns.addToCart({
          title: trigger.getAttribute('data-eds-cart-title'),
          sku: trigger.getAttribute('data-eds-cart-sku'),
          price: trigger.getAttribute('data-eds-cart-price'),
          variantId: trigger.getAttribute('data-eds-cart-variant-id'),
          quantity: parseInt(trigger.getAttribute('data-eds-cart-qty'), 10) || 1
        });
        break;
      case 'send-inquiry':
        document.dispatchEvent(new CustomEvent('eds:send-inquiry', { detail: { trigger: trigger } }));
        break;
    }
  }

  /* === Mount / Unmount lifecycle ===
     Order matters: stagger runs before reveal so children carry their --eds-delay
     before the reveal observer subscribes. */
  var controllerMap = [
    { sel: '[data-eds-cad-field]', fn: initCADField },
    { sel: '[data-eds-cad-trace]', fn: initCADTrace },
    { sel: '[data-eds-stagger]', fn: initStagger },
    { sel: '[data-eds-reveal]', fn: initReveal },
    { sel: '[data-eds-spec-card]', fn: initSpecCard },
    { sel: '[data-eds-drag-rail]', fn: initDragRail },
    { sel: '[data-eds-magnetic]', fn: initMagnetic },
    { sel: '[data-eds-smart-header]', fn: initSmartHeader },
    { sel: '[data-eds-product-image]', fn: initProductImage },
    { sel: '[data-eds-counter]', fn: initCounter },
    { sel: '[data-eds-line-trace]', fn: initLineTrace }
  ];

  function init(root) {
    root = root || document;
    for (var c = 0; c < controllerMap.length; c++) {
      var entry = controllerMap[c];
      var nodes = root.querySelectorAll ? root.querySelectorAll(entry.sel) : [];
      for (var i = 0; i < nodes.length; i++) {
        try { entry.fn(nodes[i]); } catch (e) {}
      }
      if (root.matches && root.matches(entry.sel)) {
        try { entry.fn(root); } catch (e) {}
      }
    }
    initAnnouncementBar();
  }

  function destroy(root) {
    root = root || document;
    var combined = '[data-eds-cad-field],[data-eds-cad-trace],[data-eds-stagger],[data-eds-reveal],[data-eds-spec-card],[data-eds-drag-rail],[data-eds-magnetic],[data-eds-smart-header],[data-eds-product-image],[data-eds-counter],[data-eds-line-trace],.eds-announce-bar';
    var nodes = root.querySelectorAll ? root.querySelectorAll(combined) : [];
    for (var i = 0; i < nodes.length; i++) {
      runCleanup(nodes[i]);
    }
    if (root.matches && root.matches(combined)) {
      runCleanup(root);
    }
  }

  /* === Boot === */
  function boot() {
    init(document);
    document.addEventListener('click', onActionClick);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    init(ev.target);
  });
  document.addEventListener('shopify:section:unload', function (ev) {
    destroy(ev.target);
  });
  document.addEventListener('shopify:block:select', function (ev) {
    init(ev.target);
  });

  /* === Expose === */
  window.EDSPatterns = EDSPatterns;
  window.EDSMotion = {
    _loaded: true,
    init: init,
    destroy: destroy,
    isInitialized: isInitialized,
    toast: showToast
  };

}(window, document));